/**
 * Modal Web Component
 * ------------------------------------------
 * This component defines a custom HTML element <modal-component>
 * that provides an accessible and interactive modal dialog UI.
 *
 * Attributes:
 * - alignment: left, center
 * - backdrop-style: blur, solid, transparent
 * - close-on-backdrop-click: true, false
 * - show-close-button: true, false
 * - anchor: top, middle, bottom
 * - anchor-bottom-on-mobile: true, false
 * - open: true, false
 *
 * Slots:
 * - hero: optional
 * - header: optional (appears in top bar, for compact modals without hero)
 * - title: optional (appears below hero)
 * - description: optional
 * - body: optional
 * - primary-action: optional
 * - secondary-action: optional
 *
 * Events:
 * - modal-open: when modal is opened
 * - modal-close: when modal is closed (via any method)
 * - modal-cancel: when ESC key is pressed
 * - modal-backdrop-click: when backdrop is clicked
 *
 * Example:
 *  <modal-component alignment="left" backdrop-style="blur">
 *    <span slot="title">Modal Title</span>
 *    <span slot="description">Your action was completed.</span>
 *    <div slot="body"><p>Additional details...</p></div>
 *    <button-component slot="secondary-action" label="Cancel"></button-component>
 *    <button-component slot="primary-action" label="Confirm"></button-component>
 *  </modal-component>
 *
 * JavaScript API Example:
 *  const modal = document.querySelector('modal-component');
 *  modal.open();  // Open the modal
 *  modal.close(); // Close the modal
 */

const Modal = (function () {
  // --- Create a <template> element that defines the modal structure and styles ---
  let template = document.createElement("template");
  template.innerHTML = `
  <style>
    :host {
      /* Figma Design Tokens */
      --modal-bg: #f5f5f5;
      --modal-border-radius: 2rem;
      --modal-max-width: 25rem; /* 400px */
      --modal-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      
      /* Backdrop */
      --backdrop-color: rgba(0, 0, 0, 0.4);
      --backdrop-blur: blur(12px);
      
      /* Spacing */
      --spacing-xs: 0.375rem; /* 6px */
      --spacing-sm: 0.5rem; /* 8px */
      --spacing-lg: 0.75rem; /* 12px */
      --spacing-xl: 1rem; /* 16px */
      --spacing-2xl: 1.25rem; /* 20px */
      --spacing-3xl: 1.5rem; /* 24px */
      
      /* Close button */
      --close-button-size: 2rem; // 32px
      --close-button-color: #292929;
      --close-button-hover-bg: rgba(0, 0, 0, 0.05);
      --close-button-focus-outline: 2px solid #1570ef;
      
      /* Typography */
      --title-size: 1.5rem;
      --title-weight: 500;
      --title-line-height: 2rem;
      --title-color: #000000;
      --description-size: 1rem;
      --description-weight: 400;
      --description-line-height: 1.5rem;
      --description-color: #424242;
      --font-family: 'Inter', sans-serif;
      
      /* Animation */
      --animation-duration: 0.25s;
    }

    /* Native dialog element styling */
    dialog {
      position: fixed;
      inset: 0;
      margin: 0;
      padding: 0;
      border: none;
      background: transparent;
      max-width: 100vw;
      max-height: 100vh;
      max-height: 100dvh; /* Dynamic viewport height for mobile browsers */
      width: 100vw;
      height: 100vh;
      height: 100dvh; /* Dynamic viewport height for mobile browsers */
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity var(--animation-duration) ease;
    }

    dialog:focus {
      outline: none;
      box-sizing: border-box;
    }

    /* Ensure dialog is hidden when not open - overrides the display: flex above */
    dialog:not([open]) {
      display: none;
    }

    dialog[open] {
      opacity: 1;
    }


    /* Anchor positioning for dialog */
    dialog[data-anchor="top"] {
      align-items: flex-start;
    }

    dialog[data-anchor="top"] .modal-container {
      margin-top: 2rem;
      margin-bottom: auto;
    }

    dialog[data-anchor="middle"] {
      align-items: center;
    }

    dialog[data-anchor="bottom"] {
      align-items: flex-end;
    }

    dialog[data-anchor="bottom"] .modal-container {
      margin-top: auto;
      margin-bottom: 2rem;
    }

    /* Mobile anchor positioning */
    @media (max-width: 768px) {
      dialog[data-anchor-mobile="bottom"] {
        align-items: flex-end;
      }
      
      dialog[data-anchor-mobile="bottom"] .modal-container {
        margin-top: auto;
        margin-bottom: 0.5rem;
      }
    }

    /* Native ::backdrop pseudo-element for backdrop styles */
    dialog::backdrop {
      background: var(--backdrop-color);
      backdrop-filter: var(--backdrop-blur);
      transition: opacity var(--animation-duration) ease;
    }

    dialog.backdrop-solid::backdrop {
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: none;
    }

    dialog.backdrop-transparent::backdrop {
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: none;
    }

    /* Respect user's motion preferences */
    @media (prefers-reduced-motion: reduce) {
      dialog {
        transition: none;
      }
      
      dialog::backdrop {
        transition: none;
      }
      
      .modal-container {
        transition: none;
        transform: none; /* Remove the scale effect entirely */
      }
      
      dialog[open] .modal-container {
        transform: none;
        
        @starting-style {
          transform: none;
        }
      }
    }

    /* Modal container (the actual modal box) */
    .modal-container {
      background: var(--modal-bg);
      border-radius: var(--modal-border-radius);
      box-shadow: var(--modal-shadow);
      max-width: var(--modal-max-width);
      /* Subtract 2rems from the height and width to account for the margin */
      max-height: calc(100vh - 2rem);
      max-height: calc(100dvh - 2rem); /* Dynamic viewport height for mobile browsers */
      width: calc(100% - 1rem);
      display: flex;
      flex-direction: column;
      padding-bottom: var(--spacing-3xl);
      opacity: 0;
      transform: scale(0.95);
      transition: opacity var(--animation-duration) ease, transform var(--animation-duration) ease;
      position: relative;
      overflow: hidden;
      pointer-events: auto;
      box-sizing: border-box;
    }

    dialog[open] .modal-container {
      opacity: 1;
      transform: scale(1);

      @starting-style {
        opacity: 0;
        transform: scale(0.95);
      }
    }

    /* Top Bar - contains header title and close button */
    .modal-top-bar {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--spacing-xl);
      flex-shrink: 0;
      min-height: var(--close-button-size);
      padding: var(--spacing-xl) var(--spacing-2xl) var(--spacing-sm);
    }

    .modal-top-bar-title {
      flex: 1;
      min-width: 0;
      margin: 0;
      font-family: var(--font-family);
      font-size: var(--title-size);
      font-weight: var(--title-weight);
      line-height: var(--title-line-height);
      color: var(--title-color);
      letter-spacing: -0.02em;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .close-button {
      width: var(--close-button-size);
      height: var(--close-button-size);
      border: none;
      background: transparent;
      color: var(--close-button-color);
      cursor: pointer;
      border-radius: 0.5rem; /* 8px */
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      flex-shrink: 0;
      margin-left: auto; /* Always push to right, even when header is empty */
      transition: background-color 0.15s ease;
    }

    .close-button:hover {
      background-color: var(--close-button-hover-bg);
    }

    .close-button:focus,
    .close-button:focus-visible {
      outline: var(--close-button-focus-outline);
      outline-offset: 2px;
    }

    .close-button.hidden {
      display: none;
    }

    .close-button svg {
      width: 1.5rem; /* 24px */
      height: 1.5rem; /* 24px */
      stroke: currentColor;
    }

    /* Content Section - contains hero, title, description, and body */
    .modal-content {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      gap: var(--spacing-3xl);
      padding: 0 var(--spacing-2xl);
      min-height: 0;
      overflow-y: auto;
    }

    .modal-content:empty {
      display: none;
    }

    /* Hero Image */
    .modal-hero {
      width: 100%;
      flex-shrink: 0;
    }

    .modal-hero ::slotted(*) {
      width: 100%;
      display: block;
    }

    .modal-hero ::slotted(img) {
      width: 100%;
      max-height: 300px; /* Adjust based on your design needs */
      height: auto;
      display: block;
      object-fit: contain;
    }

    .modal-header {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      flex-shrink: 0;
    }

    .modal-title {
      margin: 0;
      font-family: var(--font-family);
      font-size: var(--title-size);
      font-weight: var(--title-weight);
      line-height: var(--title-line-height);
      color: var(--title-color);
      letter-spacing: -0.02em;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .modal-description {
      margin: 0;
      font-family: var(--font-family);
      font-size: var(--description-size);
      font-weight: var(--description-weight);
      line-height: var(--description-line-height);
      color: var(--description-color);
      letter-spacing: -0.005em;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .modal-container[data-alignment="center"] .modal-header {
      align-items: center;
      text-align: center;
    }

    .modal-container[data-alignment="left"] .modal-header {
      align-items: flex-start;
      text-align: left;
    }

    .modal-body {
      flex-shrink: 0;
    }

    .modal-footer {
      padding: 0 var(--spacing-xl);
      display: flex;
      gap: var(--spacing-lg);
      flex-shrink: 0;
      margin-top: var(--spacing-3xl);
    }

    ::slotted(button-component) {
      flex: 1;
      display: block;
      min-width: 0;
    }
  </style>

  <dialog data-alignment="left">
    <div class="modal-container">
      <div class="modal-top-bar">
        <h2 class="modal-top-bar-title">
          <slot name="header"></slot>
        </h2>
        <button class="close-button" aria-label="Close modal" type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="modal-content">
        <div class="modal-hero">
          <slot name="hero"></slot>
        </div>

        <div class="modal-header">
          <h2 class="modal-title">
            <slot name="title"></slot>
          </h2>
          <p class="modal-description">
            <slot name="description"></slot>
          </p>
        </div>

        <div class="modal-body">
          <slot name="body"></slot>
        </div>
      </div>

      <div class="modal-footer">
        <slot name="secondary-action"></slot>
        <slot name="primary-action"></slot>
      </div>
    </div>
  </dialog>
  `;

  /**
   * Modal custom element definition.
   */
  class Modal extends HTMLElement {
    // --- Observed attributes for reactivity ---
    static get observedAttributes() {
      return [
        "open",
        "alignment",
        "backdrop-style",
        "close-on-backdrop-click",
        "show-close-button",
        "anchor",
        "anchor-bottom-on-mobile",
      ];
    }

    constructor() {
      super();

      this.attachShadow({ mode: "open" });
      this.shadowRoot.appendChild(template.content.cloneNode(true));

      this.dialog = this.shadowRoot.querySelector("dialog");
      this.modalContainer = this.shadowRoot.querySelector(".modal-container");
      this.closeButton = this.shadowRoot.querySelector(".close-button");

      // Track focus for restoration
      this.triggerElement = null;

      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.handleClose = this.handleClose.bind(this);

      // Unified slot visibility tracking configuration
      this.slotContainers = [
        {
          container: this.shadowRoot.querySelector(".modal-top-bar-title"),
          slot: this.shadowRoot.querySelector(
            '.modal-top-bar slot[name="header"]',
          ),
        },
        {
          container: this.shadowRoot.querySelector(".modal-hero"),
          slot: this.shadowRoot.querySelector('.modal-hero slot[name="hero"]'),
        },
        {
          container: this.shadowRoot.querySelector(".modal-header"),
          slots: [
            this.shadowRoot.querySelector('.modal-header slot[name="title"]'),
            this.shadowRoot.querySelector(
              '.modal-header slot[name="description"]',
            ),
          ],
        },
        {
          container: this.shadowRoot.querySelector(".modal-body"),
          slot: this.shadowRoot.querySelector('.modal-body slot[name="body"]'),
        },
        {
          container: this.shadowRoot.querySelector(".modal-footer"),
          slots: [
            this.shadowRoot.querySelector(
              '.modal-footer slot[name="primary-action"]',
            ),
            this.shadowRoot.querySelector(
              '.modal-footer slot[name="secondary-action"]',
            ),
          ],
        },
      ];

      // Bind slot change listeners for visibility
      this.slotContainers.forEach((config) => {
        config.visibilityHandler = this.updateContainerVisibility.bind(
          this,
          config,
        );

        if (config.slots) {
          config.slots.forEach((slot) => {
            slot.addEventListener("slotchange", config.visibilityHandler);
          });
        } else {
          config.slot.addEventListener("slotchange", config.visibilityHandler);
        }

        // Initial visibility check
        this.updateContainerVisibility(config);
      });

      // Bind ARIA update to header/title/description slot changes
      this.handleAriaSlotChange = this.updateAriaAttributes.bind(this);
      this.headerSlot = this.shadowRoot.querySelector('slot[name="header"]');
      this.titleSlot = this.shadowRoot.querySelector('slot[name="title"]');
      this.descriptionSlot = this.shadowRoot.querySelector(
        'slot[name="description"]',
      );
      this.headerSlot.addEventListener("slotchange", this.handleAriaSlotChange);
      this.titleSlot.addEventListener("slotchange", this.handleAriaSlotChange);
      this.descriptionSlot.addEventListener(
        "slotchange",
        this.handleAriaSlotChange,
      );
    }

    connectedCallback() {
      this.dialog.addEventListener("click", this.handleDialogClick);
      this.dialog.addEventListener("cancel", this.handleCancel);
      this.dialog.addEventListener("close", this.handleClose);
      this.closeButton.addEventListener("click", this.handleCloseButtonClick);

      this.updateAlignment();
      this.updateBackdropStyle();
      this.updateCloseButtonVisibility();
      this.updateAnchor();
      this.updateMobileAnchor();

      if (this.hasAttribute("open")) {
        this.open();
      }
    }

    disconnectedCallback() {
      this.dialog.removeEventListener("click", this.handleDialogClick);
      this.dialog.removeEventListener("cancel", this.handleCancel);
      this.dialog.removeEventListener("close", this.handleClose);
      this.closeButton.removeEventListener(
        "click",
        this.handleCloseButtonClick,
      );

      this.slotContainers.forEach((config) => {
        if (config.slots) {
          config.slots.forEach((slot) => {
            slot.removeEventListener("slotchange", config.visibilityHandler);
          });
        } else {
          config.slot.removeEventListener(
            "slotchange",
            config.visibilityHandler,
          );
        }
      });

      this.headerSlot.removeEventListener(
        "slotchange",
        this.handleAriaSlotChange,
      );
      this.titleSlot.removeEventListener(
        "slotchange",
        this.handleAriaSlotChange,
      );
      this.descriptionSlot.removeEventListener(
        "slotchange",
        this.handleAriaSlotChange,
      );

      if (this.dialog.open) {
        this.dialog.close();
      }
    }

    /**
     * Called when an observed attribute changes.
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      switch (name) {
        case "open":
          if (newValue !== null) {
            this.open();
          } else {
            this.close();
          }
          break;
        case "alignment":
          this.updateAlignment();
          break;
        case "backdrop-style":
          this.updateBackdropStyle();
          break;
        case "close-on-backdrop-click":
          // No immediate action needed, checked in handler
          break;
        case "show-close-button":
          this.updateCloseButtonVisibility();
          break;
        case "anchor":
          this.updateAnchor();
          break;
        case "anchor-bottom-on-mobile":
          this.updateMobileAnchor();
          break;
      }
    }

    /**
     * Opens the modal.
     */
    open() {
      if (!this.isConnected || this.dialog.open) return;

      // Store the currently focused element
      this.triggerElement = document.activeElement;

      // Lock background scroll
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      this.dialog.showModal();

      // Set initial focus
      requestAnimationFrame(() => this.setInitialFocus());

      this.dispatchEvent(
        new CustomEvent("modal-open", { bubbles: true, composed: true }),
      );
    }

    /**
     * Closes the modal.
     */
    close() {
      if (!this.dialog.open) return;

      this.dialog.close();

      if (this.hasAttribute("open")) {
        this.removeAttribute("open");
      }
    }

    /**
     * Handles dialog clicks (for backdrop click detection).
     */
    handleDialogClick(event) {
      // Only close if clicking the dialog itself (not modal content)
      if (event.target === this.dialog) {
        this.dispatchEvent(
          new CustomEvent("modal-backdrop-click", {
            bubbles: true,
            composed: true,
          }),
        );

        // Close if configured to do so
        const closeOnBackdropClick = this.getAttribute(
          "close-on-backdrop-click",
        );
        if (closeOnBackdropClick !== "false") {
          this.close();
        }
      }
    }

    /**
     * Handles close button clicks.
     */
    handleCloseButtonClick() {
      this.close();
    }

    /**
     * Handles the cancel event (ESC key press).
     * Native dialog fires this before closing.
     */
    handleCancel() {
      this.dispatchEvent(
        new CustomEvent("modal-cancel", {
          bubbles: true,
          composed: true,
        }),
      );
      // Note: Native ESC handling closes the dialog automatically,
      // but we call close() explicitly to ensure cleanup happens
      // (especially for synthetic events in tests)
      this.close();
    }

    /**
     * Handles the close event (when dialog closes).
     * Native dialog fires this after closing.
     */
    handleClose() {
      // Restore background scroll
      document.body.style.overflow = this.previousOverflow || "";

      // Restore focus to trigger element
      requestAnimationFrame(() => this.restoreFocusTrigger());

      this.dispatchEvent(
        new CustomEvent("modal-close", { bubbles: true, composed: true }),
      );
    }

    /**
     * Sets initial focus when modal opens.
     * Priority: primary-action > close button > dialog
     */
    setInitialFocus() {
      if (!this.isConnected || !this.dialog.open) return;

      const focusable = this.findFocusableInSlot("primary-action");
      if (focusable) {
        focusable.focus();
        return;
      }

      if (!this.closeButton.classList.contains("hidden")) {
        this.closeButton.focus();
        return;
      }

      this.dialog.focus();
    }

    /**
     * Finds the first focusable element in a named slot.
     */
    findFocusableInSlot(slotName) {
      const slot = this.shadowRoot.querySelector(`slot[name="${slotName}"]`);
      const elements = slot?.assignedElements({ flatten: true }) || [];

      const selectors =
        "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

      for (const el of elements) {
        if (el.matches?.(selectors)) return el;
        const child = el.querySelector(selectors);
        if (child) return child;
      }
      return null;
    }

    /**
     * Restores focus to the trigger element.
     */
    restoreFocusTrigger() {
      if (!this.isConnected) return;
      this.triggerElement?.focus?.();
      this.triggerElement = null;
    }

    /**
     * Updates backdrop style based on attribute.
     */
    updateBackdropStyle() {
      const style = this.getAttribute("backdrop-style") || "blur";

      this.dialog.classList.remove(
        "backdrop-blur",
        "backdrop-solid",
        "backdrop-transparent",
      );

      // Apply class based on style (default is blur, so no class needed)
      if (style === "solid") {
        this.dialog.classList.add("backdrop-solid");
      } else if (style === "transparent") {
        this.dialog.classList.add("backdrop-transparent");
      }
    }

    /**
     * Updates close button visibility based on attribute.
     */
    updateCloseButtonVisibility() {
      const showCloseButton = this.getAttribute("show-close-button");

      if (showCloseButton === "false") {
        this.closeButton.classList.add("hidden");
      } else {
        this.closeButton.classList.remove("hidden");
      }
    }

    /**
     * Updates container visibility based on whether its slot(s) have content.
     */
    updateContainerVisibility(config) {
      let hasContent = false;

      if (config.slots) {
        hasContent = config.slots.some((slot) => this.slotHasContent(slot));
      } else {
        hasContent = this.slotHasContent(config.slot);
      }

      config.container.style.display = hasContent ? "" : "none";
    }

    /**
     * Checks if a slot has content.
     */
    slotHasContent(slot) {
      const assignedNodes = slot.assignedNodes({ flatten: true });
      return assignedNodes.some((node) => {
        return (
          node.nodeType === Node.ELEMENT_NODE ||
          (node.nodeType === Node.TEXT_NODE &&
            node.textContent.trim().length > 0)
        );
      });
    }

    /**
     * Updates modal anchor position based on attribute.
     */
    updateAnchor() {
      const anchor = this.getAttribute("anchor") || "middle";
      this.dialog.setAttribute("data-anchor", anchor);
    }

    /**
     * Updates mobile anchor position based on attribute.
     */
    updateMobileAnchor() {
      const anchorBottomOnMobile = this.getAttribute("anchor-bottom-on-mobile");

      if (anchorBottomOnMobile === "true") {
        this.dialog.setAttribute("data-anchor-mobile", "bottom");
      } else {
        this.dialog.removeAttribute("data-anchor-mobile");
      }
    }

    /**
     * Updates ARIA attributes for accessibility.
     * Called via slotchange events when header/title/description content changes.
     */
    updateAriaAttributes() {
      const headerElement = this.shadowRoot.querySelector(
        ".modal-top-bar-title",
      );
      const titleElement = this.shadowRoot.querySelector(".modal-title");
      const descElement = this.shadowRoot.querySelector(".modal-description");

      // Set aria-labelledby - prefer header slot, fall back to title slot
      if (this.headerSlot.assignedNodes().length > 0 && headerElement) {
        headerElement.id = "modal-top-bar-title";
        this.dialog.setAttribute("aria-labelledby", "modal-top-bar-title");
      } else if (this.titleSlot.assignedNodes().length > 0 && titleElement) {
        titleElement.id = "modal-title";
        this.dialog.setAttribute("aria-labelledby", "modal-title");
      } else {
        this.dialog.removeAttribute("aria-labelledby");
      }

      // Set aria-describedby if description slot has content
      if (this.descriptionSlot.assignedNodes().length > 0 && descElement) {
        descElement.id = "modal-description";
        this.dialog.setAttribute("aria-describedby", "modal-description");
      } else {
        this.dialog.removeAttribute("aria-describedby");
      }
    }

    /**
     * Updates text alignment.
     */
    updateAlignment() {
      const alignment = this.getAttribute("alignment") || "left";
      this.dialog.setAttribute("data-alignment", alignment);
      this.modalContainer.setAttribute("data-alignment", alignment);
    }
  }

  // --- Register custom element ---
  customElements.define("modal-component", Modal);

  return Modal;
})();

export { Modal };
